/**
 * The frame every pre-session screen sits in — sign in, reset, invitation,
 * MFA, the organization picker.
 *
 * Matched to the approved prototype rather than invented. A first pass here
 * chose its own direction (warm paper, a serif wordmark) and it was wrong: the
 * product has a design system, its brand is navy and blue, and the entry
 * screen is a two-column card with the form on a navy panel beside a
 * photograph. Tokens live in `styles.css` as the prototype defines them.
 *
 * **A SKYLINE, DELIBERATELY, NOT A BUILDING.** The founder's rule is that
 * building imagery comes from real photographs of real buildings, because a
 * rendered building is a claim about a place that does not exist in a product
 * whose whole argument is that it does not invent things. A city skyline is
 * not a claim about any property: it is the market this product serves, and
 * nobody can mistake it for a client's building. That is why it sidesteps the
 * rule rather than bending it.
 *
 * It is generated, and it says so — the approved prototype carries the same
 * disclosure on its own entry image (`entry-synthetic`). One line, quiet, at
 * the foot of the panel. Delete `<p className="entry-synthetic">` to remove it.
 *
 * **WebP with a JPEG behind it.** This is the first thing anybody loads, and
 * the source was a 2.3 MB PNG; it is 190 KB and 253 KB now. `<picture>` rather
 * than a bare `<img>` because the sign-in page is the one screen that must
 * render on whatever the user has.
 *
 * On a narrow screen it moves above the form and shrinks to a banner: it is
 * decoration, and decoration does not go first on a phone where the form is
 * the reason somebody opened the page.
 */
import skylineJpg from '../assets/entry-skyline.jpg';
import skylineWebp from '../assets/entry-skyline.webp';

export function EntryLayout({ children }: { children: React.ReactNode }) {
  return (
    <main className="entry">
      <div className="entry-card">
        <div className="entry-form-side">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">
              <svg viewBox="0 0 32 32" width="21" height="21" focusable="false">
                <path
                  d="M9 23V11l7-3 7 3v12"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 23h18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="brand-text">
              Premise
              <br />
              Sense
            </span>
          </div>

          {children}
        </div>

        {/* `div`, not `figure`: a figure is content with a caption, and this
            is decoration. `alt=""` for the same reason — a screen reader
            should reach the form, not a description of a picture that says
            nothing the form does not. */}
        <div className="entry-photo">
          <picture>
            <source srcSet={skylineWebp} type="image/webp" />
            <img src={skylineJpg} alt="" width={1122} height={1402} />
          </picture>
          <p className="entry-synthetic">Illustration. Not a photograph of a real property.</p>
        </div>
      </div>
    </main>
  );
}
